import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { FileBarChart, Eye, RotateCcw, MapPin, Zap } from 'lucide-react';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  generating: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

const ReportsPage = () => {
  const [viewHtml, setViewHtml] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');

  const { data: reports, isLoading, refetch } = useQuery({
    queryKey: ['admin-reports', filter],
    queryFn: async () => {
      let q = supabase
        .from('report_requests')
        .select('*')
        .order('requested_at', { ascending: false })
        .limit(100);
      if (filter !== 'all') q = q.eq('status', filter);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });

  const handleResend = async (id: string) => {
    await supabase.from('report_requests').update({ status: 'pending' }).eq('id', id);
    refetch();
  };

  const stats = {
    total: reports?.length || 0,
    pending: reports?.filter(r => r.status === 'pending').length || 0,
    sent: reports?.filter(r => r.status === 'sent').length || 0,
    failed: reports?.filter(r => r.status === 'failed').length || 0,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileBarChart className="w-6 h-6" style={{ color: 'var(--blue)' }} />
          <h1 className="text-2xl font-fraunces font-bold" style={{ color: 'var(--ink)' }}>Report Generator</h1>
        </div>
        {stats.pending > 0 && (
          <button
            onClick={async () => {
              toast.loading('Generating pending reports…', { id: 'gen' });
              const { error } = await supabase.functions.invoke('generate-report', { body: { force: true } });
              if (error) {
                toast.error('Generation failed: ' + error.message, { id: 'gen' });
              } else {
                toast.success('Reports generated! Refreshing…', { id: 'gen' });
                refetch();
              }
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{ background: 'var(--blue)', color: '#fff' }}
          >
            <Zap className="w-4 h-4" />
            Generate Now ({stats.pending})
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'var(--blue)' },
          { label: 'Pending', value: stats.pending, color: '#d97706' },
          { label: 'Sent', value: stats.sent, color: '#16a34a' },
          { label: 'Failed', value: stats.failed, color: '#dc2626' },
        ].map(s => (
          <div key={s.label} className="rounded-xl p-4" style={{ background: 'var(--white)', border: '1px solid var(--line)' }}>
            <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--slate)' }}>{s.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {['all', 'pending', 'generating', 'sent', 'failed'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: filter === f ? 'var(--blue)' : 'var(--white)',
              color: filter === f ? '#fff' : 'var(--slate)',
              border: `1px solid ${filter === f ? 'var(--blue)' : 'var(--line)'}`,
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <p className="text-center py-12" style={{ color: 'var(--slate)' }}>Loading…</p>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--white)', border: '1px solid var(--line)' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports?.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.name || '—'}</TableCell>
                  <TableCell className="text-sm">{r.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {r.source === 'configurator' ? 'Configurator' : 'ROI Calculator'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {(r.country || r.city) ? (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[r.city, r.country].filter(Boolean).join(', ')}
                      </span>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.requested_at ? format(new Date(r.requested_at), 'dd MMM yyyy HH:mm') : '—'}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[r.status] || ''}`}>
                      {r.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {r.report_html && (
                        <button
                          onClick={() => setViewHtml(r.report_html)}
                          className="p-1.5 rounded-md hover:bg-muted transition-colors"
                          title="View Report"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      {(r.status === 'sent' || r.status === 'failed') && (
                        <button
                          onClick={() => handleResend(r.id)}
                          className="p-1.5 rounded-md hover:bg-muted transition-colors"
                          title="Resend"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {(!reports || reports.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8" style={{ color: 'var(--slate)' }}>
                    No report requests yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Report viewer modal */}
      {viewHtml && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setViewHtml(null)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-auto m-4 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between p-4 border-b" style={{ background: 'var(--white)' }}>
              <h3 className="font-semibold" style={{ color: 'var(--ink)' }}>Report Preview</h3>
              <button
                onClick={() => setViewHtml(null)}
                className="px-3 py-1 rounded-md text-sm"
                style={{ background: 'var(--off-white)', color: 'var(--slate)' }}
              >
                Close
              </button>
            </div>
            <div className="p-6" dangerouslySetInnerHTML={{ __html: viewHtml }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
